import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash-es';

import { UserService } from '../services/core.user.service';
import { GfTranslateService } from '../services/core.translate.service';
import { ConstantsService } from '../services/core.constants.service';
import { StateService } from '@uirouter/angular';
import { doGetPromise } from './core.httpclient-utils';
import { AppComponent } from '../modules/home/app.component';
import { SiteSplashComponent } from '../modules/site-splash/site-splash.component';
import { TopMenuComponent } from '../modules/topmenu/topmenu/topmenu.component';
import { SearchComponent } from '../modules/search/search.component';
import { ProjectBrowseComponent } from '../modules/project-browse/project-browse.component';
import setSiteConfig from './core.set-site-config';
import { SiteConfigService } from '../services/core.site-config.service';
import { FeatureFlagService } from '../services/core.feature-flags.service';
import { NotFoundComponent } from '../modules/home/not-found/not-found.component';
import { LicenseComponent } from '../modules/home/license/license.component';
import { ErrorBlockComponent } from '../modules/home/error-block/error-block.component';

export const CORE_STATES: any[] = [
    {
        name: 'root',
        url: '',
        redirectTo: 'home'
    },
    {
        name: 'error',
        url: '/error',
        component: ErrorBlockComponent
    },
    {
        name: 'home',
        url: '/?sitepage&highlight&msg',
        params: {
            sitepage: {
                value: null,
                squash: true,
                raw: true,
                dynamic: true
            },
            highlight: {
                value: null,
                squash: true,
                raw: true,
                dynamic: true
            },
            msg: {
                value: null,
                squash: true,
                raw: true,
                dynamic: true
            }
        },
        resolve: [
            {
                token: 'siteConfig',
                deps: [Window, StateService, HttpClient, SiteConfigService],
                resolveFn: (
                    $window: Window,
                    $state: StateService,
                    httpClient: HttpClient,
                    siteConfigService: SiteConfigService) => {

                    const siteConfigUrl = '/site/config';
                    return doGetPromise(httpClient, siteConfigUrl)
                        .then((data) => {
                            // This call handles messy legacy stuff with $window
                            setSiteConfig(data, $window);

                            // This call centralizes the data
                            siteConfigService.setSiteConfig(data);

                            return data;
                        }, (response) => {
                            console.log(response);
                            $state.go('error');
                        });
                }
            },
            {
                token: 'user',
                deps: [Window, UserService, StateService],
                resolveFn: ($window: Window, userService: UserService, $state: StateService) => {
                    return userService.session.recover()
                    .then( (result) => {
                        if (result.success && result.newSSO){
                            console.log('SSO new user redirect');
                            $state.go('home.user.register');
                            return userService.session.getUser();
                        }
                        // If session recovery successful, return the user
                        return userService.session.getUser();
                    }, (err) => {
                        console.log("Session recovery failed:", err);
                        // Set nobody user but don't redirect - let individual routes handle auth
                        userService.setNobody();
                        return userService.session.getUser();
                    });
                }
            },
            {
                token: 'userPreferences',
                deps: [UserService, 'user'],
                resolveFn: (userService: UserService, user: any) => {
                    return userService.preference.init();
                }
            },
            {
                token: 'featureFlags',
                deps: [FeatureFlagService, UserService, 'user', 'siteConfig'],
                resolveFn: (featureFlagService: FeatureFlagService, userService: UserService) => {
                    return featureFlagService.init(userService.session.getUnixName());
                }
            },
            {
                token: 'languageSetup',
                deps: [GfTranslateService, UserService, ConstantsService],
                resolveFn: (gfTranslateService: GfTranslateService, userService: UserService, constantsService: ConstantsService) => {
                    var def = _.get(navigator, 'language',
                        _.get(navigator, 'userLanguage')
                    );

                    if (def) {
                        def = def.split('-')[0];
                    } else {
                        def = "en";
                    }

                    var lang = userService.profile.getLanguage() || def;

                    // Import moment here to avoid circular dependencies
                    import('moment').then(momentModule => {
                        // Moment uses namespace export, access it directly
                        if (momentModule && momentModule.locale) {
                            momentModule.locale(lang);
                        }
                    });

                    return Promise.all([
                        gfTranslateService.setLanguage(lang),
                        constantsService.loadLanguages()
                    ]);
                }
            }
        ],
        data: {
            searchContext: ['user', 'project']
        },
        views: {
            '': {
                component: AppComponent
            },
            'topMenu@home': {
                component: TopMenuComponent
            },
            'contentarea@home': {
                component: SiteSplashComponent
            }
        }
    },
    {
        name: 'home.search',
        url: 'search?page_num&page_size&q&s&p&c',
        resolve: [
            {
                token: 'project',
                deps: [],
                resolveFn: async () => {
                    return false;
                }
            }
        ],
        params: {
            page_num: {
                value: '1',
                squash: true,
                dynamic: true
            },
            page_size: {
                value: '20',
                squash: true,
                dynamic: true
            },
            // q: Query - The search keywords/text entered by the user
            q: {
                value: '',
                squash: true,
                raw: true,
                dynamic: true
            },
            // s: Sections - Comma-separated list of content types to search (search type buttons)
            // e.g., 'trackeritem,scmcommit,wiki,project,user,scm,docman,forum'
            s: {
                value: 'trackeritem,scmcommit,wiki',
                squash: true,
                raw: true,
                dynamic: true
            },
            // p: Project - The project unix name to scope the search to (null for site-wide)
            p: {
                value: null,
                squash: true,
                raw: true,
                dynamic: true
            },
            // c: Categories - Comma-separated list of Trove category IDs to filter results
            c: {
                value: null,
                squash: true,
                raw: true,
                dynamic: true
            }
        },
        views: {
            'contentarea@home': {
                component: SearchComponent
            }
        }
    },
    {
        name: 'home.projects',
        url: 'projects?sort_field&sort_dir&page_num&page_size&q',
        resolve: [
            {
                token: 'project',
                deps: [],
                resolveFn: async () => {
                    return false;
                }
            }
        ],
        params: {
            page_num: {
                value: '1',
                squash: true
            },
            page_size: {
                value: '20',
                squash: true
            },
            sort_field: {
                value: 'activity',
                squash: true
            },
            sort_dir: {
                value: 'desc',
                squash: true
            },
            q: {
                value: '',
                squash: true
            }
        },
        views: {
            'contentarea@home': {
                component: ProjectBrowseComponent
            }
        }
    },
    {
        name: 'home.license',
        url: 'invalid-license',
        views: {
            'contentarea@home': {
                component: LicenseComponent
            }
        }
    },
    {
        name: 'home.not-found', 
        url: 'not-found',
        views: {
            'contentarea@home': {
                component: NotFoundComponent
            }
        }
    }
];
